import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Send, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, Spinner, Empty, Avatar, Time, Input, Btn } from "@/forum/ui";

const Conversation = ({ username }) => {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const bottom = useRef(null);

  const load = useCallback(() => api.get(`/forum/messages/${username}`).then((r) => { setData(r.data); refresh(); }).catch((e) => toast.error(errorText(e))), [username, refresh]);

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => { bottom.current?.scrollIntoView({ block: "nearest" }); }, [data?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data: m } = await api.post(`/forum/messages/${username}`, { content: text });
      setData((d) => ({ ...d, messages: [...d.messages, m] }));
      setText("");
    } catch (ex) { toast.error(errorText(ex)); } finally { setBusy(false); }
  };

  if (!data) return <Spinner />;
  return (
    <Panel className="flex h-[70vh] flex-col" data-testid="conversation">
      <div className="flex items-center gap-3 border-b border-white/[0.06] px-5 py-4">
        <Avatar user={data.user} size="sm" />
        <Link to={`/forum/u/${data.user.username}`} className="font-display font-bold hover:text-[var(--glow)]" data-testid="conversation-username">{data.user.username}</Link>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
        {data.messages.length === 0 && <p className="text-center font-mono text-xs text-white/30">Még nincs üzenet. Írj elsőként!</p>}
        {data.messages.map((m) => (
          <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
            <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${m.mine ? "bg-white text-black" : "bg-white/[0.06] text-white/90"}`}>
              <p className="whitespace-pre-wrap">{m.content}</p>
              <Time iso={m.created_at} className={`mt-1 block ${m.mine ? "text-black/50" : ""}`} />
            </div>
          </div>
        ))}
        <div ref={bottom} />
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-white/[0.06] p-3" data-testid="message-form">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Üzenet…" maxLength={5000} data-testid="message-input" />
        <Btn type="submit" disabled={busy || !text.trim()} data-testid="message-send"><Send size={14} /></Btn>
      </form>
    </Panel>
  );
};

export default function MessagesPage() {
  const { username } = useParams();
  const [convos, setConvos] = useState(null);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useEffect(() => { api.get("/forum/messages").then((r) => setConvos(r.data)); }, [username]);

  const start = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/forum/messages/${q.trim()}`);
  };

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div>
        <p className="eyebrow mb-3">Privát</p>
        <h1 className="font-display text-3xl font-bold">Üzenetek</h1>
      </div>
      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <div className="space-y-3">
          <form onSubmit={start} className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2" data-testid="new-message-form">
            <Search size={14} className="text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Új beszélgetés: felhasználónév" className="w-full bg-transparent text-sm outline-none placeholder:text-white/30" data-testid="new-message-username" />
          </form>
          <Panel className="overflow-hidden">
            {!convos ? <Spinner /> : convos.length === 0 ? <Empty text="Még nincs beszélgetésed" testId="no-conversations" /> : (
              <div className="divide-y divide-white/[0.05]">
                {convos.map((c) => (
                  <Link key={c.other_id} to={`/forum/messages/${c.user?.username}`} className={`flex items-center gap-3 px-4 py-3 transition-colors hover:bg-white/[0.03] ${username === c.user?.username ? "bg-white/[0.05]" : ""}`} data-testid={`conversation-${c.user?.username}`}>
                    <Avatar user={c.user} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <p className="truncate text-sm font-semibold">{c.user?.username}</p>
                        <Time iso={c.at} />
                      </div>
                      <p className="truncate text-xs text-white/50">{c.last}</p>
                    </div>
                    {c.unread > 0 && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-[var(--glow)] px-1 font-mono text-[10px] font-bold text-black">{c.unread}</span>}
                  </Link>
                ))}
              </div>
            )}
          </Panel>
        </div>
        {username ? <Conversation username={username} /> : <Empty text="Válassz egy beszélgetést, vagy indíts újat" testId="select-conversation" />}
      </div>
    </div>
  );
}
